/**
 * The step-up refusal codes, with no imports so `HttpApiService` can read them without an import cycle
 * (`twoFactorErrors` reads errors through `apiError`, which imports `HttpApiService`). The meaning of each
 * code is documented in `twoFactorErrors.ts`.
 */

/** The 10th wrong PIN: the PIN needs a new one AND the server has signed the user out of every session. */
export const SECURITY_PIN_RESET_REQUIRED_CODE = "SECURITY_PIN_RESET_REQUIRED";

/** Too many wrong codes: 15 minutes for either method, or a new PIN after the PIN's hard lock. */
export const STEP_UP_LOCK_ERROR_CODES = ["STEP_UP_LOCKED", SECURITY_PIN_RESET_REQUIRED_CODE] as const;

/** The email backup sent its one-time code: not a refusal, the challenge switches to "check your email". */
export const STEP_UP_EMAIL_SENT_CODE = "STEP_UP_EMAIL_SENT";

/** A code was sent less than a minute ago (or six this hour): the message says when to ask again. */
export const STEP_UP_EMAIL_COOLDOWN_CODE = "STEP_UP_EMAIL_COOLDOWN";

/** The emailed code expired, was used, or took 5 wrong guesses: a new one has to be requested. */
export const STEP_UP_EMAIL_CODE_INVALID_CODE = "STEP_UP_EMAIL_CODE_INVALID";

/** The 403 of a challenge asking for an email code while the backup is off. Not a missing permission. */
export const STEP_UP_EMAIL_NOT_ENABLED_CODE = "STEP_UP_EMAIL_NOT_ENABLED";

/** The 401s the email backup answers inside a challenge. */
export const STEP_UP_EMAIL_CHALLENGE_CODES = [
  STEP_UP_EMAIL_SENT_CODE,
  STEP_UP_EMAIL_COOLDOWN_CODE,
  STEP_UP_EMAIL_CODE_INVALID_CODE,
] as const;

export const TWO_FACTOR_CODE_ERROR_CODES = [
  "TWO_FACTOR_CODE_REQUIRED",
  "TWO_FACTOR_CODE_INVALID",
  ...STEP_UP_LOCK_ERROR_CODES,
  ...STEP_UP_EMAIL_CHALLENGE_CODES,
] as const;

export const TWO_FACTOR_ENROLLMENT_ERROR_CODE = "TWO_FACTOR_REQUIRED";

/**
 * Codes that refuse what the person typed, not the session: the token was valid, so a refresh would change
 * nothing and the replay would only spend another attempt — against the step-up lock, or against the login
 * lock that a wrong current password now counts toward. For the email backup the replay is worse: it would
 * send a second email. Only the 401s among them ever reach the refresh path; the 400s and 403s are listed so
 * the list reads as the whole contract.
 */
export const NON_SESSION_401_CODES: readonly string[] = [
  ...TWO_FACTOR_CODE_ERROR_CODES,
  STEP_UP_EMAIL_NOT_ENABLED_CODE,
  "PASSWORD_INCORRECT",
  "PASSWORD_REQUIRED",
  "SECURITY_PIN_REUSED",
  "ACCOUNT_LOCKED",
  "STEP_UP_EMAIL_REQUIRES_METHOD",
  "STEP_UP_EMAIL_NOT_AVAILABLE",
];
